// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/utils/InvoicePrint.ts
//
// این یک فایل جدید است. در مسیر بالا کپی کنید (نیاز به ساخت فایل جدید).
// ============================================================

import jalaliMoment from "jalali-moment";

// ===== توابع کمکی فرمت =====
const fmt = (n: any) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDateBoth = (dateStr: string): string => {
  if (!dateStr) return "";
  const m = jalaliMoment(dateStr, "YYYY-MM-DD").locale("en");
  const greg = `${m.format("D")} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m.format("M"))-1]} ${m.format("YYYY")}`;
  const j = jalaliMoment(dateStr, "YYYY-MM-DD").locale("fa").format("jYYYY/jMM/jDD");
  return `${j} • ${greg}`;
};

// زمان فعلی به وقت کابل (UTC+4:30)
const getKabulTime = (): string => {
  const now = new Date();
  // محاسبه زمان کابل: UTC + 4:30
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const kabulTime = new Date(utc + (4.5 * 3600000));
  const h = kabulTime.getHours();
  const m = kabulTime.getMinutes();
  const ampm = h >= 12 ? "بعد از ظهر" : "صبح";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
};

// تبدیل عدد به حروف (فارسی)
const numberToWords = (num: number): string => {
  if (num === 0) return "صفر";
  const ones = ["", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"];
  const teens = ["ده", "یازده", "دوازده", "سیزده", "چهارده", "پانزده", "شانزده", "هفده", "هجده", "نوزده"];
  const tens = ["", "", "بیست", "سی", "چهل", "پنجاه", "شصت", "هفتاد", "هشتاد", "نود"];
  const hundreds = ["", "صد", "دویست", "سه صد", "چهارصد", "پنجصد", "ششصد", "هفتصد", "هشتصد", "نهصد"];

  const convertGroup = (n: number): string => {
    if (n === 0) return "";
    let result = "";
    const h = Math.floor(n / 100);
    const t = Math.floor((n % 100) / 10);
    const o = n % 10;
    if (h > 0) result += hundreds[h];
    const tn = (t * 10) + o;
    if (tn >= 10 && tn < 20) {
      if (h > 0) result += " و ";
      result += teens[tn - 10];
    } else {
      if (t > 0) {
        if (h > 0) result += " و ";
        result += tens[t];
      }
      if (o > 0) {
        if (h > 0 || t > 0) result += " و ";
        result += ones[o];
      }
    }
    return result;
  };

  const intPart = Math.floor(Math.abs(num));
  const billion = Math.floor(intPart / 1000000000);
  const million = Math.floor((intPart % 1000000000) / 1000000);
  const thousand = Math.floor((intPart % 1000000) / 1000);
  const rest = intPart % 1000;

  let result = "";
  if (billion > 0) result += convertGroup(billion) + " میلیارد";
  if (million > 0) { if (result) result += " و "; result += convertGroup(million) + " میلیون"; }
  if (thousand > 0) { if (result) result += " و "; result += convertGroup(thousand) + " هزار"; }
  if (rest > 0) { if (result) result += " و "; result += convertGroup(rest); }
  if (num < 0) result = "منفی " + result;
  return result;
};

// CSS مشترک برای A5 - دیزاین رسمی و باکلاس - پر کننده کل صفحه
const COMMON_CSS = `
  @page { size: A5; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: 148mm; height: 210mm; }
  body { 
    font-family: 'Vazirmatn', 'Tahoma', sans-serif; 
    direction: rtl; 
    color: #1a1a1a;
    background: white;
    padding: 8mm 8mm 0 8mm;
    font-size: 10.5px;
    line-height: 1.55;
    position: relative;
    height: 210mm;
    display: flex;
    flex-direction: column;
  }
  .doc-frame {
    border: 1px solid #1e3a8a;
    border-radius: 3px;
    padding: 12px 14px;
    background: white;
    position: relative;
    box-shadow: 0 0 0 3px white, 0 0 0 4px #1e3a8a;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .doc-header {
    text-align: center;
    padding-bottom: 8px;
    border-bottom: 2px double #1e3a8a;
    margin-bottom: 10px;
    position: relative;
  }
  .doc-header::before, .doc-header::after {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 1px;
    background: #1e3a8a;
  }
  .doc-header::before { right: 0; }
  .doc-header::after { left: 0; }
  .doc-title {
    font-size: 18px;
    font-weight: 800;
    color: #1e3a8a;
    letter-spacing: 1.5px;
    margin-bottom: 1px;
  }
  .doc-subtitle {
    font-size: 8px;
    color: #94a3b8;
    font-weight: 600;
    letter-spacing: 4px;
    text-transform: uppercase;
  }
  .doc-meta {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 8px;
    margin-bottom: 10px;
    padding: 7px 10px;
    background: #fafafa;
    border: 1px solid #e5e5e5;
    border-radius: 3px;
  }
  .doc-meta-item { 
    font-size: 9.5px; 
    border-right: 2px solid #1e3a8a;
    padding-right: 8px;
  }
  .doc-meta-item:last-child { border-right: none; }
  .doc-meta-item strong { 
    display: block;
    color: #64748b; 
    font-size: 8.5px;
    font-weight: 700;
    margin-bottom: 3px;
    letter-spacing: 0.5px;
  }
  .doc-meta-item span { 
    color: #1a1a1a;
    font-weight: 700;
    font-size: 10px;
    direction: ltr;
    text-align: right;
    display: inline-block;
  }
  .doc-meta-item.rtl span { direction: rtl; text-align: right; }
  .party-box {
    background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
    border: 1px solid #d4d4d4;
    border-right: 4px solid #1e3a8a;
    border-radius: 3px;
    padding: 8px 12px;
    margin-bottom: 10px;
  }
  .party-label {
    font-size: 8.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    margin-bottom: 3px;
  }
  .party-name {
    font-size: 13px;
    font-weight: 800;
    color: #1a1a1a;
    margin-bottom: 2px;
  }
  .party-info {
    font-size: 9.5px;
    color: #525252;
    line-height: 1.6;
  }
  .items-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 8px;
    font-size: 9.5px;
    border: 1px solid #1e3a8a;
  }
  .items-table th {
    background: #1e3a8a;
    color: white;
    padding: 6px 5px;
    font-weight: 700;
    font-size: 9.5px;
    text-align: center;
    border-left: 1px solid #3151a8;
    letter-spacing: 0.3px;
  }
  .items-table th:last-child { border-left: none; }
  .items-table td {
    padding: 6px 5px;
    border-bottom: 1px solid #e5e5e5;
    border-left: 1px solid #f0f0f0;
    text-align: center;
    color: #1a1a1a;
  }
  .items-table td:last-child { border-left: none; }
  .items-table tr:last-child td { border-bottom: none; }
  .items-table tr:nth-child(even) td { background: #fafafa; }
  .items-table .product-name { text-align: right; font-weight: 600; padding-right: 8px; }
  .items-table .price-col { font-weight: 700; color: #064e3b; font-family: 'Vazirmatn', monospace; }
  .totals {
    margin-top: 6px;
    margin-bottom: 8px;
    border: 1px solid #e5e5e5;
    border-radius: 3px;
    overflow: hidden;
  }
  .totals-row {
    display: flex;
    justify-content: space-between;
    padding: 5px 12px;
    font-size: 10.5px;
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
  }
  .totals-row:last-child { border-bottom: none; }
  .totals-row.subtotal { color: #525252; font-weight: 600; }
  .totals-row.discount { color: #c2410c; font-weight: 600; }
  .totals-row.grand {
    background: linear-gradient(135deg, #1e3a8a, #1e40af);
    color: white;
    font-size: 12px;
    font-weight: 800;
    padding: 8px 14px;
    letter-spacing: 0.3px;
  }
  .amount-words {
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-right: 3px solid #d97706;
    padding: 6px 11px;
    margin-bottom: 8px;
    font-size: 9.5px;
    color: #78350f;
    border-radius: 2px;
    line-height: 1.7;
  }
  .amount-words strong { 
    color: #451a03; 
    font-weight: 700;
    margin-left: 4px;
  }
  .balance-box {
    background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
    border: 1.5px solid #0284c7;
    border-radius: 3px;
    padding: 8px 14px;
    margin-bottom: 8px;
    text-align: center;
  }
  .balance-label {
    font-size: 9px;
    color: #075985;
    font-weight: 700;
    margin-bottom: 3px;
    letter-spacing: 0.3px;
  }
  .balance-value {
    font-size: 14px;
    font-weight: 800;
    color: #0c4a6e;
  }
  .balance-status {
    font-size: 9px;
    font-weight: 700;
    margin-right: 6px;
    padding: 2px 7px;
    border-radius: 3px;
    letter-spacing: 0.3px;
  }
  .balance-status.debtor { background: #fee2e2; color: #991b1b; }
  .balance-status.creditor { background: #d1fae5; color: #065f46; }
  .balance-status.zero { background: #e2e8f0; color: #475569; }
  .notes-box {
    background: #fafafa;
    border: 1px dashed #cbd5e1;
    border-radius: 2px;
    padding: 6px 10px;
    margin-bottom: 8px;
    font-size: 9.5px;
    color: #525252;
    line-height: 1.7;
  }
  .notes-box strong { 
    color: #1a1a1a; 
    display: block; 
    margin-bottom: 3px;
    font-size: 9.5px;
    font-weight: 700;
  }
  .signatures {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: auto;
    padding-top: 14px;
  }
  .sig-box {
    flex: 1;
    text-align: center;
    border: 1px dashed #94a3b8;
    border-radius: 3px;
    padding: 22px 6px 10px;
    min-height: 75px;
    position: relative;
    background: #fafafa;
  }
  .sig-label {
    position: absolute;
    top: -7px;
    right: 50%;
    transform: translateX(50%);
    background: white;
    padding: 0 8px;
    font-size: 8.5px;
    font-weight: 700;
    color: #475569;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }
  .system-footer {
    position: fixed;
    bottom: 3mm;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 7px;
    color: #1e3a8a;
    font-weight: 700;
    letter-spacing: 0.4px;
    white-space: nowrap;
  }
  .system-footer span { color: #1e3a8a; }
  @media print {
    body { padding: 8mm 8mm 0 8mm; height: 210mm; }
    .doc-frame { box-shadow: 0 0 0 3px white, 0 0 0 4px #1e3a8a; }
    .system-footer { color: #1e3a8a !important; }
  }
`;

// ============================================================
// 🧾 فاکتور فروش
// ============================================================
export function printSaleInvoice(sale: any, customer: any, customerBalance: number = 0) {
  const items = sale.items || [];
  const total = parseFloat(sale.total_amount || 0);
  const discount = parseFloat(sale.discount || 0);
  const net = parseFloat(sale.net_amount || 0);
  const currency = sale.customer_currency || "USD";
  const issueTime = getKabulTime();
  
  const customerInfo = customer ? [
    customer.phone && `📞 ${customer.phone}`,
    customer.address && `📍 ${customer.address}`,
    customer.code && `کد: ${customer.code}`,
  ].filter(Boolean).join(" | ") : "";
  
  const html = `
    <!DOCTYPE html><html dir="rtl" lang="fa"><head>
    <meta charset="UTF-8">
    <title>فاکتور ${sale.serial}</title>
    <link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700;800&display=swap" rel="stylesheet">
    <style>${COMMON_CSS}</style>
    </head><body>
    <div class="doc-frame">
      <div class="doc-header">
        <div class="doc-title">فاکتور فروش</div>
        <div class="doc-subtitle">SALES INVOICE</div>
      </div>
      
      <div class="doc-meta">
        <div class="doc-meta-item rtl">
          <strong>شماره فاکتور</strong>
          <span>${sale.serial}</span>
        </div>
        <div class="doc-meta-item rtl">
          <strong>تاریخ صدور</strong>
          <span>${formatDateBoth(sale.date)}</span>
        </div>
        <div class="doc-meta-item rtl">
          <strong>ساعت صدور</strong>
          <span>${issueTime} (به وقت کابل)</span>
        </div>
      </div>
      
      <div class="party-box">
        <div class="party-label">مشخصات خریدار</div>
        <div class="party-name">${sale.customer_name || "-"}</div>
        ${customerInfo ? `<div class="party-info">${customerInfo}</div>` : ""}
      </div>
      
      <table class="items-table">
        <thead>
          <tr>
            <th style="width:28px">#</th>
            <th style="width:auto">شرح کالا/خدمت</th>
            <th style="width:48px">واحد</th>
            <th style="width:55px">تعداد</th>
            <th style="width:75px">قیمت واحد</th>
            <th style="width:85px">مجموع (${currency})</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((it: any, i: number) => `
            <tr>
              <td>${i + 1}</td>
              <td class="product-name">${it.product_name || "-"}</td>
              <td>${it.unit || "-"}</td>
              <td>${fmt(it.quantity)}</td>
              <td class="price-col">${fmt(it.unit_price)}</td>
              <td class="price-col">${fmt(it.total_price)}</td>
            </tr>
          `).join("")}
          ${items.length === 0 ? `<tr><td colspan="6" style="color:#94a3b8">— بدون اقلام —</td></tr>` : ""}
        </tbody>
      </table>
      
      <div class="totals">
        <div class="totals-row subtotal">
          <span>جمع کل (${currency})</span>
          <span>${fmt(total)}</span>
        </div>
        ${discount > 0 ? `
          <div class="totals-row discount">
            <span>تخفیف (${currency})</span>
            <span>− ${fmt(discount)}</span>
          </div>
        ` : ""}
        <div class="totals-row grand">
          <span>مبلغ خالص قابل پرداخت</span>
          <span>${fmt(net)} ${currency}</span>
        </div>
      </div>
      
      <div class="amount-words">
        <strong>به حروف:</strong> ${numberToWords(net)} ${currency === "USD" ? "دالر آمریکایی" : currency === "AFN" ? "افغانی" : currency === "EUR" ? "یورو" : currency === "PKR" ? "کلدار پاکستانی" : currency === "IRR" ? "ریال ایران" : currency === "TRY" ? "لیر ترکیه" : currency}
      </div>
      
      ${sale.notes ? `
        <div class="notes-box">
          <strong>توضیحات:</strong>
          ${sale.notes}
        </div>
      ` : ""}
      
      <div class="signatures">
        <div class="sig-box">
          <div class="sig-label">مهر و امضای مسئول مالی</div>
        </div>
        <div class="sig-box">
          <div class="sig-label">امضای خریدار</div>
        </div>
      </div>
    </div>
    
    <div class="system-footer"><span>نرم افزار حسابداری گلبرگ - ورژن 2026.3.01.04 — mustafasaberi3344@gmail.com</span></div>
    </body></html>
  `;
  
  const w = window.open("", "_blank");
  if (w) {
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 500);
  }
}

// ============================================================
// 🟢 رسید دریافت پول از مشتری
// ============================================================
export function printReceiptIn(payment: any, customer: any, customerBalance: number = 0) {
  const amount = parseFloat(payment.receipt_amount || 0);
  const currency = payment.receipt_currency || "USD";
  const customerCurrency = payment.customer_currency || currency;
  const customerAmount = parseFloat(payment.customer_amount || amount);
  const issueTime = getKabulTime();
  
  const balanceStatus = customerBalance > 0 ? "بدهکار" : customerBalance < 0 ? "بستانکار" : "تسویه";
  const balanceClass = customerBalance > 0 ? "debtor" : customerBalance < 0 ? "creditor" : "zero";
  
  const customerInfo = customer ? [
    customer.phone && `📞 ${customer.phone}`,
    customer.code && `کد: ${customer.code}`,
  ].filter(Boolean).join(" | ") : "";
  
  const html = `
    <!DOCTYPE html><html dir="rtl" lang="fa"><head>
    <meta charset="UTF-8">
    <title>رسید دریافت ${payment.serial}</title>
    <link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700;800&display=swap" rel="stylesheet">
    <style>${COMMON_CSS}
      .doc-title { color: #047857 !important; }
      .doc-header { border-bottom-color: #047857 !important; }
      .doc-frame { border-color: #047857 !important; box-shadow: 0 0 0 3px white, 0 0 0 4px #047857 !important; }
      .doc-header::before, .doc-header::after { background: #047857 !important; }
      .doc-meta-item { border-right-color: #047857 !important; }
      .party-box { border-right-color: #047857 !important; }
      .items-table { border-color: #047857 !important; }
      .items-table th { background: #047857 !important; }
      .totals-row.grand { background: linear-gradient(135deg, #047857, #059669) !important; }
      .balance-status.debtor { background: #d1fae5; color: #065f46; }
      .balance-status.creditor { background: #fee2e2; color: #991b1b; }
    </style>
    </head><body>
    <div class="doc-frame">
      <div class="doc-header">
        <div class="doc-title">رسید دریافت پول</div>
        <div class="doc-subtitle">RECEIPT OF PAYMENT</div>
      </div>
      
      <div class="doc-meta">
        <div class="doc-meta-item rtl">
          <strong>شماره رسید</strong>
          <span>${payment.serial}</span>
        </div>
        <div class="doc-meta-item rtl">
          <strong>تاریخ دریافت</strong>
          <span>${formatDateBoth(payment.date)}</span>
        </div>
        <div class="doc-meta-item rtl">
          <strong>ساعت صدور</strong>
          <span>${issueTime} (به وقت کابل)</span>
        </div>
      </div>
      
      <div class="party-box">
        <div class="party-label">دریافت شد از (پرداخت‌کننده)</div>
        <div class="party-name">${payment.customer_name || "-"}</div>
        ${customerInfo ? `<div class="party-info">${customerInfo}</div>` : ""}
      </div>
      
      <table class="items-table">
        <thead>
          <tr>
            <th>شرح</th>
            <th style="width:120px">مبلغ دریافتی</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="product-name">واریز به حساب: ${payment.account_name || "-"}</td>
            <td class="price-col">${fmt(amount)} ${currency}</td>
          </tr>
          ${currency !== customerCurrency ? `
            <tr>
              <td class="product-name">معادل (با نرخ ${fmt(payment.exchange_rate || 1)})</td>
              <td class="price-col">${fmt(customerAmount)} ${customerCurrency}</td>
            </tr>
          ` : ""}
        </tbody>
      </table>
      
      <div class="totals">
        <div class="totals-row grand">
          <span>مبلغ کل دریافت شده</span>
          <span>${fmt(amount)} ${currency}</span>
        </div>
      </div>
      
      <div class="amount-words">
        <strong>به حروف:</strong> ${numberToWords(amount)} ${currency === "USD" ? "دالر آمریکایی" : currency === "AFN" ? "افغانی" : currency === "EUR" ? "یورو" : currency === "PKR" ? "کلدار پاکستانی" : currency === "IRR" ? "ریال ایران" : currency === "TRY" ? "لیر ترکیه" : currency}
      </div>
      
      <div class="balance-box">
        <div class="balance-label">بیلانس کل مشتری پس از این دریافت</div>
        <div class="balance-value">
          ${fmt(Math.abs(customerBalance))} ${customerCurrency}
          <span class="balance-status ${balanceClass}">${balanceStatus}</span>
        </div>
      </div>
      
      ${payment.notes ? `
        <div class="notes-box">
          <strong>توضیحات:</strong>
          ${payment.notes}
        </div>
      ` : ""}
      
      <div class="signatures">
        <div class="sig-box">
          <div class="sig-label">امضای دریافت‌کننده</div>
        </div>
        <div class="sig-box">
          <div class="sig-label">امضای پرداخت‌کننده</div>
        </div>
      </div>
    </div>
    
    <div class="system-footer"><span>نرم افزار حسابداری گلبرگ - ورژن 2026.3.01.04 — mustafasaberi3344@gmail.com</span></div>
    </body></html>
  `;
  
  const w = window.open("", "_blank");
  if (w) {
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 500);
  }
}

// ============================================================
// 🔴 رسید پرداخت پول به فروشنده
// ============================================================
export function printReceiptOut(payment: any, vendor: any, vendorBalance: number = 0) {
  const amount = parseFloat(payment.payment_amount || 0);
  const currency = payment.payment_currency || "USD";
  const vendorCurrency = payment.vendor_currency || currency;
  const vendorAmount = parseFloat(payment.vendor_amount || amount);
  const issueTime = getKabulTime();
  
  // برای فروشنده، علامت متفاوت است: ما به آن‌ها بدهکاریم اگر balance منفی باشد
  // در سیستم vendor balance: مثبت = ما به vendor بدهکار، منفی = vendor به ما بدهکار
  const balanceStatus = vendorBalance > 0 ? "ما بدهکار" : vendorBalance < 0 ? "ما بستانکار" : "تسویه";
  const balanceClass = vendorBalance > 0 ? "debtor" : vendorBalance < 0 ? "creditor" : "zero";
  
  const vendorInfo = vendor ? [
    vendor.phone && `📞 ${vendor.phone}`,
    vendor.code && `کد: ${vendor.code}`,
  ].filter(Boolean).join(" | ") : "";
  
  const html = `
    <!DOCTYPE html><html dir="rtl" lang="fa"><head>
    <meta charset="UTF-8">
    <title>رسید پرداخت ${payment.serial}</title>
    <link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700;800&display=swap" rel="stylesheet">
    <style>${COMMON_CSS}
      .doc-title { color: #b45309 !important; }
      .doc-header { border-bottom-color: #b45309 !important; }
      .doc-frame { border-color: #b45309 !important; box-shadow: 0 0 0 3px white, 0 0 0 4px #b45309 !important; }
      .doc-header::before, .doc-header::after { background: #b45309 !important; }
      .doc-meta-item { border-right-color: #b45309 !important; }
      .party-box { border-right-color: #b45309 !important; }
      .items-table { border-color: #b45309 !important; }
      .items-table th { background: #b45309 !important; }
      .totals-row.grand { background: linear-gradient(135deg, #b45309, #d97706) !important; }
    </style>
    </head><body>
    <div class="doc-frame">
      <div class="doc-header">
        <div class="doc-title">رسید پرداخت پول</div>
        <div class="doc-subtitle">PAYMENT VOUCHER</div>
      </div>
      
      <div class="doc-meta">
        <div class="doc-meta-item rtl">
          <strong>شماره رسید</strong>
          <span>${payment.serial}</span>
        </div>
        <div class="doc-meta-item rtl">
          <strong>تاریخ پرداخت</strong>
          <span>${formatDateBoth(payment.date)}</span>
        </div>
        <div class="doc-meta-item rtl">
          <strong>ساعت صدور</strong>
          <span>${issueTime} (به وقت کابل)</span>
        </div>
      </div>
      
      <div class="party-box">
        <div class="party-label">پرداخت شد به (دریافت‌کننده)</div>
        <div class="party-name">${payment.vendor_name || "-"}</div>
        ${vendorInfo ? `<div class="party-info">${vendorInfo}</div>` : ""}
      </div>
      
      <table class="items-table">
        <thead>
          <tr>
            <th>شرح</th>
            <th style="width:120px">مبلغ پرداختی</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="product-name">برداشت از حساب: ${payment.account_name || "-"}</td>
            <td class="price-col">${fmt(amount)} ${currency}</td>
          </tr>
          ${currency !== vendorCurrency ? `
            <tr>
              <td class="product-name">معادل (با نرخ ${fmt(payment.exchange_rate || 1)})</td>
              <td class="price-col">${fmt(vendorAmount)} ${vendorCurrency}</td>
            </tr>
          ` : ""}
        </tbody>
      </table>
      
      <div class="totals">
        <div class="totals-row grand">
          <span>مبلغ کل پرداخت شده</span>
          <span>${fmt(amount)} ${currency}</span>
        </div>
      </div>
      
      <div class="amount-words">
        <strong>به حروف:</strong> ${numberToWords(amount)} ${currency === "USD" ? "دالر آمریکایی" : currency === "AFN" ? "افغانی" : currency === "EUR" ? "یورو" : currency === "PKR" ? "کلدار پاکستانی" : currency === "IRR" ? "ریال ایران" : currency === "TRY" ? "لیر ترکیه" : currency}
      </div>
      
      <div class="balance-box">
        <div class="balance-label">بیلانس کل فروشنده پس از این پرداخت</div>
        <div class="balance-value">
          ${fmt(Math.abs(vendorBalance))} ${vendorCurrency}
          <span class="balance-status ${balanceClass}">${balanceStatus}</span>
        </div>
      </div>
      
      ${payment.notes ? `
        <div class="notes-box">
          <strong>توضیحات:</strong>
          ${payment.notes}
        </div>
      ` : ""}
      
      <div class="signatures">
        <div class="sig-box">
          <div class="sig-label">امضای پرداخت‌کننده</div>
        </div>
        <div class="sig-box">
          <div class="sig-label">امضای دریافت‌کننده</div>
        </div>
      </div>
    </div>
    
    <div class="system-footer"><span>نرم افزار حسابداری گلبرگ - ورژن 2026.3.01.04 — mustafasaberi3344@gmail.com</span></div>
    </body></html>
  `;
  
  const w = window.open("", "_blank");
  if (w) {
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 500);
  }
}